/* @flow */

import * as React from 'react';
import styled from '@emotion/styled';
import classNames from 'classnames';
import numeral from 'numeral';
import CurrencyIcon from 'app/components/commons/CurrencyIcon';
import DateComponent from '../commons/DateComponent';
import DiscountPromo from '../commons/DiscountPromo';
import Responsive from '../responsive/Responsive';
import RewriteLink from '../routing/RewriteLink';
import Row from '../commons/Row';

type Props = {
  amount?: string,
  balance: number,
  bonusAmount?: string,
  categoryId: number,
  categoryName: string,
  challengeId?: string,
  date: string,
  discount?: number,
  entryId?: number,
  id: number,
  label?: string,
  sheet: Object,
  type: string,
  typeId: number,
  paymentTransactionLabel?: string
};

const defaultProps = {
  amount: undefined,
  bonusAmount: undefined,
  challengeId: undefined,
  discount: undefined,
  entryId: undefined,
  label: undefined,
  paymentTransactionLabel: undefined
};

const NameAuxillary = styled.span(({ theme }) => ({
  color: theme.color.primary,
  display: 'block',
  [theme.mediaQuery.tabletLandscapeAndUp]: {
    display: 'inline-block',
    paddingLeft: '1em'
  }
}));

const Stack = styled.div({
  display: 'flex',
  flexDirection: 'column'
});

const Debit = styled.span(({ theme }) => ({
  color: theme.color.danger
}));

const Credit = styled.span(({ theme }) => ({
  color: theme.color.success
}));

const FinePrint = styled.span(({ theme }) => ({
  fontSize: theme.fontSize.tiny
}));

function TransactionRow({
  amount,
  balance,
  bonusAmount,
  categoryId,
  categoryName,
  challengeId,
  date,
  discount,
  entryId,
  label,
  id,
  sheet: { classes },
  type,
  typeId,
  paymentTransactionLabel
}: Props) {
  const isTransfer = categoryId === 9 || categoryId === 10;

  return (
    <RewriteLink to={challengeId ? `/challenge/${challengeId}/${entryId}` : '#'}>
      <Row>
        <div className={classes.transactionRow}>
          <div className={`${classes.tableCell} ${classes.tableCell__Small}`}>
            <div>#{id}</div>
            <div
              className={classNames(classes.pill, {
                [classes.pill_debit]: typeId === 1,
                [classes.pill_credit]: typeId !== 1
              })}
            >
              {type}
            </div>
          </div>
          <div className={`${classes.tableCell} ${classes.tableCell__Large}`}>
            <Responsive desktop>
              {isDesktop => (
                <DateComponent date={date} format={isDesktop ? 'ddd DD MMM YYYY' : 'DD-MMM-YYYY'} />
              )}
            </Responsive>
            <div className={classes.subtext}>
              <DateComponent date={date} format="HH:mm:ss" />
            </div>
          </div>
          <div className={classes.tableCellWrapper}>
            <div className={classes.tableCellNested}>
              <div className={classes.tableCellNestedFirst}>
                <div className={`${classes.tableCell} ${classes.tableCell__Main}`}>
                  <span>
                    {isTransfer ? 'Transfer' : categoryName}
                    {paymentTransactionLabel && ` - ${paymentTransactionLabel}`}
                    {entryId && <NameAuxillary>Ticket #{entryId}</NameAuxillary>}
                  </span>
                </div>
              </div>
              <div className={classes.tableCellNestedLast}>
                <div className={`${classes.tableCell} ${classes.tableCell__Default}`}>
                  {!!discount && (
                    <span className={classes.iconWrapper}>
                      <DiscountPromo discount={discount} />
                    </span>
                  )}
                  {typeId === 1 ? (
                    <Stack>
                      {amount && (
                        <Debit>
                          -<CurrencyIcon />
                          {amount}
                        </Debit>
                      )}
                      {bonusAmount && (
                        <Debit>
                          -<CurrencyIcon />
                          {bonusAmount} <FinePrint>(bonus)</FinePrint>
                        </Debit>
                      )}
                    </Stack>
                  ) : (
                    <Stack>
                      {amount && (
                        <Credit>
                          <CurrencyIcon />
                          {amount}
                        </Credit>
                      )}
                      {bonusAmount && (
                        <Credit>
                          <CurrencyIcon />
                          {bonusAmount} <FinePrint>(bonus)</FinePrint>
                        </Credit>
                      )}
                    </Stack>
                  )}
                </div>
                <div className={`${classes.tableCell} ${classes.tableCell__Small}`}>
                  {numeral(balance).format('$0.00')}
                </div>
              </div>
            </div>
            <div className={classes.tableCell}>
              <div className={classes.subtext}>{isTransfer ? categoryName : label}</div>
            </div>
          </div>
        </div>
      </Row>
    </RewriteLink>
  );
}

TransactionRow.defaultProps = defaultProps;

export default TransactionRow;
